import { Mail, MessageCircle, Instagram, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * Design Philosophy: Minimalismo Corporativo
 * - Logo e informações de contato bem organizadas
 * - Links rápidos para navegação
 * - Espaço em branco generoso
 * - Ícones minimalistas
 */

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "GO Marketing Digital",
    "image": "https://www.gomarketingdigital.com.br/manus-storage/logo-go-marketing_c5116202.webp", // Substitua pela URL real do seu logo
    "url": "https://www.gomarketingdigital.com.br/",
    "telephone": "+5511971328412",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rua Exemplo, 123", // Substitua pelo endereço real
      "addressLocality": "São Paulo",
      "addressRegion": "SP",
      "postalCode": "01234-567", // Substitua pelo CEP real
      "addressCountry": "BR"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday"
        ],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/gomatrketingdigital/",
      "https://www.tiktok.com/@gomatrketingdigital",
      "https://www.facebook.com/profile.php?id=100091109542770"
    ]
  };

  return (
    <footer className="bg-[#1f6f9f] text-white py-8 sm:py-12 md:py-16">
      <Helmet>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
        />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-12">

          {/* Logo */}
          <div className="flex flex-col items-center sm:items-start">
            <h2 className="font-semibold text-base sm:text-lg md:text-xl lg:text-xl mb-2 sm:mb-4">GO Marketing Digital</h2>
            <p className="text-xs sm:text-sm mb-2">Agência de Marketing Jurídico</p>
            <p className="text-xs sm:text-sm font-semibold mb-2">Aumente Seus Leads em 40% em 90 Dias</p>
            <p className="text-xs sm:text-sm leading-relaxed">Somos uma Agência de Marketing Jurídico. Transformamos SEO, Tráfego Pago e Sites em novos clientes para o seu escritório, com estratégias pensadas para o mercado jurídico, não fórmulas genéricas.</p>
          </div>

          {/* Links Rápidos */}
          <div className="text-center sm:text-left">
            <h2 className="font-semibold text-base sm:text-lg md:text-xl lg:text-xl mb-2 sm:mb-4">Links Rápidos</h2>
            <ul className="space-y-1.5 sm:space-y-2">
              <li>
                <a href="/" className="text-white hover:text-blue-50 transition-colors text-base sm:text-lg md:text-xl lg:text-xl">
                  Início
                </a>
              </li>
              <li>
                <a href="/#servicos" className="text-white hover:text-blue-50 transition-colors text-base sm:text-lg md:text-xl lg:text-xl">
                  Serviços
                </a>
              </li>
              <li>
                <a href="/blog" className="text-white hover:text-blue-50 transition-colors text-base sm:text-lg md:text-xl lg:text-xl">
                  Blog
                </a>
              </li>
              <li>
                <Link to="/faq" className="text-white hover:text-blue-50 transition-colors text-base sm:text-lg md:text-xl lg:text-xl">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/politica-de-privacidade" className="text-white hover:text-blue-50 transition-colors text-base sm:text-lg md:text-xl lg:text-xl">
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <a href="/#contact" className="text-white hover:text-blue-50 transition-colors text-base sm:text-lg md:text-xl lg:text-xl">
                  Contato
                </a>
              </li>
            </ul>
          </div>

          {/* Contato */}
          <div className="text-center sm:text-left">
            <h2 className="font-semibold text-base sm:text-lg md:text-xl lg:text-xl mb-2 sm:mb-4">Contato</h2>
            <ul className="space-y-2 sm:space-y-3">
              <li className="flex items-center justify-center sm:justify-start gap-2">
                <Mail size={20} className="flex-shrink-0" />
                <a
                  href="mailto:gomatrketing.digital@gmail.com"
                  className="text-white hover:text-blue-50 transition-colors text-sm sm:text-base"
                >
                  gomatrketing.digital@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Redes Sociais */}
          <div className="text-center sm:text-left">
            <h2 className="font-semibold text-base sm:text-lg md:text-xl lg:text-xl mb-2 sm:mb-4">Redes Sociais</h2>
            <div className="flex gap-3 sm:gap-4 justify-center sm:justify-start">
              <a
                href="https://www.instagram.com/gomatrketingdigital/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-blue-200 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={24} className="sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8" />
              </a>
              <a
                href="https://www.tiktok.com/@gomatrketingdigital"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-blue-200 transition-colors"
                aria-label="TikTok"
              >
                <svg className="w-6 h-6 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.1 1.82 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-.01-.01z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=100091109542770"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-blue-200 transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={24} className="sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8" />
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white border-opacity-20 pt-6 sm:pt-8">
          <div className="text-center text-white text-base sm:text-lg md:text-xl lg:text-xl">
            <p>© {currentYear} GO Marketing Digital. Todos os direitos reservados.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
